import { useEffect, useRef, useCallback, useState } from 'react';
import './DirectVideoPlayer.css';

interface StreamOption {
  url: string;
  quality: string;
  mimeType: string;
  width?: number;
  height?: number;
  hasAudio: boolean;
  hasVideo: boolean;
  bitrate?: number;
  audioTrackId?: string;
  audioTrackName?: string;
  audioLanguage?: string;
  audioIsDefault?: boolean;
}

interface DirectVideoPlayerProps {
  videoId: string;
  streams: StreamOption[];
  autoplay?: boolean;
  startAt?: number;
  onProgress?: (position: number, duration: number) => void;
  onVideoEnd?: () => void;
  onPlaybackError?: () => void;
}

function formatTime(seconds: number): string {
  const s = Math.floor(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

const HIDE_DELAY = 3000;
const STARTUP_TIMEOUT = 10000;

export default function DirectVideoPlayer({
  videoId,
  streams,
  autoplay = false,
  startAt = 0,
  onProgress,
  onVideoEnd,
  onPlaybackError,
}: DirectVideoPlayerProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressIntervalRef = useRef<number | null>(null);
  const hideTimerRef = useRef<number | null>(null);
  const onProgressRef = useRef(onProgress);
  const onVideoEndRef = useRef(onVideoEnd);
  const onPlaybackErrorRef = useRef(onPlaybackError);
  const playbackErrorReportedRef = useRef(false);
  const startupTimerRef = useRef<number | null>(null);
  const seekingRef = useRef(false);
  const clickTimerRef = useRef<number | null>(null);

  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [videoReady, setVideoReady] = useState(false);

  useEffect(() => { onProgressRef.current = onProgress; }, [onProgress]);
  useEffect(() => { onVideoEndRef.current = onVideoEnd; }, [onVideoEnd]);
  useEffect(() => { onPlaybackErrorRef.current = onPlaybackError; }, [onPlaybackError]);

  const streamUrl = streams.length > 0 ? streams[0].url : null;
  const audioStreamUrl = streams.length > 1 ? streams[1].url : null;

  // -- Set up video source --
  useEffect(() => {
    const v = videoRef.current;
    const audio = audioRef.current;
    if (!v || !streamUrl) return;

    setVideoReady(false);
    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setBuffered(0);
    playbackErrorReportedRef.current = false;

    const applyStartPosition = () => {
      if (startAt <= 0) return;
      v.currentTime = startAt;
      if (audioStreamUrl && audio) audio.currentTime = startAt;
    };

    if (v.readyState >= HTMLMediaElement.HAVE_METADATA) applyStartPosition();
    else v.addEventListener('loadedmetadata', applyStartPosition, { once: true });

    if (autoplay) {
      v.play().then(() => {
        if (audioStreamUrl && audio) {
          audio.currentTime = v.currentTime;
          audio.play().catch(() => {});
        }
      }).catch(() => {});
    }

    return () => {
      v.removeEventListener('loadedmetadata', applyStartPosition);
      v.pause();
      audio?.pause();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoId, streamUrl, audioStreamUrl]);

  // -- Video event bindings --
  useEffect(() => {
    const v = videoRef.current;
    const audio = audioRef.current;
    if (!v || !streamUrl) return;

    const onTimeUpdate = () => {
      if (!seekingRef.current) setCurrentTime(v.currentTime);
      const buf = v.buffered;
      if (buf.length > 0) setBuffered(buf.end(buf.length - 1));
      if (audioStreamUrl && audio && Math.abs(audio.currentTime - v.currentTime) > 0.35) {
        audio.currentTime = v.currentTime;
      }
    };
    const clearStartupTimer = () => {
      if (startupTimerRef.current !== null) {
        window.clearTimeout(startupTimerRef.current);
        startupTimerRef.current = null;
      }
    };
    const reportPlaybackError = () => {
      clearStartupTimer();
      if (playbackErrorReportedRef.current) return;
      playbackErrorReportedRef.current = true;
      onPlaybackErrorRef.current?.();
    };
    const onDurationChange = () => setDuration(v.duration || 0);
    const onPlay = () => {
      setPlaying(true);
      if (audioStreamUrl && audio) {
        audio.currentTime = v.currentTime;
        audio.play().catch(() => {});
      }
    };
    const onPause = () => {
      setPlaying(false);
      setShowControls(true);
      audio?.pause();
    };
    const onEnded = () => {
      setPlaying(false);
      if (onProgressRef.current && v.duration) onProgressRef.current(v.duration, v.duration);
      onVideoEndRef.current?.();
    };
    const onLoadedMetadata = () => {
      clearStartupTimer();
      setVideoReady(true);
      setDuration(v.duration || 0);
    };
    const onWaiting = () => setVideoReady(false);
    const onCanPlay = () => {
      clearStartupTimer();
      setVideoReady(true);
      if (!v.paused && audioStreamUrl && audio) {
        audio.currentTime = v.currentTime;
        audio.play().catch(() => {});
      }
    };
    const onPlaybackError = () => reportPlaybackError();

    // Some Googlevideo requests can stall at HAVE_NOTHING without emitting an
    // error event. Refresh the short-lived stream URL instead of leaving the
    // player on a permanent black 0:00 screen.
    if (v.readyState >= HTMLMediaElement.HAVE_METADATA) {
      setVideoReady(true);
      setDuration(v.duration || 0);
    } else {
      startupTimerRef.current = window.setTimeout(() => {
        if (v.readyState === HTMLMediaElement.HAVE_NOTHING) {
          console.warn('[DirectVideoPlayer] Timed out waiting for video metadata');
          reportPlaybackError();
        }
      }, STARTUP_TIMEOUT);
    }

    v.addEventListener('timeupdate', onTimeUpdate);
    v.addEventListener('durationchange', onDurationChange);
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    v.addEventListener('ended', onEnded);
    v.addEventListener('loadedmetadata', onLoadedMetadata);
    v.addEventListener('waiting', onWaiting);
    v.addEventListener('canplay', onCanPlay);
    v.addEventListener('error', onPlaybackError);
    audio?.addEventListener('error', onPlaybackError);

    return () => {
      clearStartupTimer();
      v.removeEventListener('timeupdate', onTimeUpdate);
      v.removeEventListener('durationchange', onDurationChange);
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
      v.removeEventListener('ended', onEnded);
      v.removeEventListener('loadedmetadata', onLoadedMetadata);
      v.removeEventListener('waiting', onWaiting);
      v.removeEventListener('canplay', onCanPlay);
      v.removeEventListener('error', onPlaybackError);
      audio?.removeEventListener('error', onPlaybackError);
    };
  }, [streamUrl, audioStreamUrl]);

  // -- Controls auto-hide --
  const resetHideTimer = useCallback(() => {
    setShowControls(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = window.setTimeout(() => {
      if (videoRef.current && !videoRef.current.paused) {
        setShowControls(false);
      }
    }, HIDE_DELAY);
  }, []);

  // -- Progress tracking --
  useEffect(() => {
    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    progressIntervalRef.current = window.setInterval(() => {
      const v = videoRef.current;
      if (!v || !onProgressRef.current) return;
      if (v.duration > 0) onProgressRef.current(v.currentTime, v.duration);
    }, 30000);
    return () => {
      if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    };
  }, []);

  // -- Fullscreen change listener --
  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
    };
  }, []);

  // -- Player actions --
  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().then(() => {
        const audio = audioRef.current;
        if (audioStreamUrl && audio) {
          audio.currentTime = v.currentTime;
          audio.play().catch(() => {});
        }
      }).catch(() => {});
    } else v.pause();
  }, [audioStreamUrl]);

  const toggleFullscreen = useCallback(() => {
    const v = videoRef.current;
    const el = wrapperRef.current;
    if (v && typeof (v as any).webkitEnterFullscreen === 'function') {
      if (isFullscreen) {
        (v as any).webkitExitFullscreen?.();
        document.exitFullscreen?.().catch(() => {});
      } else {
        (v as any).webkitEnterFullscreen();
      }
      return;
    }
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    else el.requestFullscreen().catch(() => {});
  }, [isFullscreen]);

  const seek = useCallback((offset: number) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.max(0, Math.min(v.duration || 0, v.currentTime + offset));
    if (audioStreamUrl && audioRef.current) audioRef.current.currentTime = v.currentTime;
  }, [audioStreamUrl]);

  const changeVolume = useCallback((delta: number) => {
    const media = audioStreamUrl ? audioRef.current : videoRef.current;
    if (!media) return;
    const next = Math.max(0, Math.min(1, media.volume + delta));
    media.volume = next;
    setVolume(next);
    if (next > 0 && media.muted) { media.muted = false; setMuted(false); }
  }, [audioStreamUrl]);

  const toggleMute = useCallback(() => {
    const media = audioStreamUrl ? audioRef.current : videoRef.current;
    if (!media) return;
    media.muted = !media.muted;
    setMuted(media.muted);
  }, [audioStreamUrl]);

  const handleVolumeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    const media = audioStreamUrl ? audioRef.current : videoRef.current;
    if (media) {
      media.volume = val;
      if (val > 0 && media.muted) { media.muted = false; setMuted(false); }
    }
    setVolume(val);
  }, [audioStreamUrl]);

  // -- Keyboard shortcuts --
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      switch (e.key) {
        case ' ':
        case 'k': case 'K':
          e.preventDefault(); togglePlay(); break;
        case 'ArrowLeft':
          e.preventDefault(); seek(-5); break;
        case 'ArrowRight':
          e.preventDefault(); seek(5); break;
        case 'j': case 'J':
          e.preventDefault(); seek(-10); break;
        case 'l': case 'L':
          e.preventDefault(); seek(10); break;
        case 'f': case 'F':
          e.preventDefault(); toggleFullscreen(); break;
        case 'Escape':
          if (isFullscreen) { e.preventDefault(); document.exitFullscreen().catch(() => {}); }
          break;
        case 'ArrowUp':
          e.preventDefault(); changeVolume(0.1); break;
        case 'ArrowDown':
          e.preventDefault(); changeVolume(-0.1); break;
        case 'm': case 'M':
          e.preventDefault(); toggleMute(); break;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [togglePlay, seek, toggleFullscreen, changeVolume, toggleMute, isFullscreen]);

  // -- Click / double-click on video area --
  const handleVideoClick = useCallback(() => {
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
      toggleFullscreen();
    } else {
      clickTimerRef.current = window.setTimeout(() => {
        clickTimerRef.current = null;
        togglePlay();
      }, 250);
    }
  }, [togglePlay, toggleFullscreen]);

  // -- Seek bar interaction --
  const handleSeekDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    seekingRef.current = true;
    const track = e.currentTarget;
    const rect = track.getBoundingClientRect();

    const applySeek = (clientX: number) => {
      const frac = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      setCurrentTime(frac * (duration || 0));
    };

    applySeek(e.clientX);

    const onMove = (ev: MouseEvent) => applySeek(ev.clientX);
    const onUp = (ev: MouseEvent) => {
      applySeek(ev.clientX);
      seekingRef.current = false;
      const v = videoRef.current;
      if (v) {
        const frac = Math.max(0, Math.min(1, (ev.clientX - rect.left) / rect.width));
        v.currentTime = frac * (v.duration || 0);
        if (audioStreamUrl && audioRef.current) audioRef.current.currentTime = v.currentTime;
      }
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [duration, audioStreamUrl]);

  const progressPct = duration > 0 ? (currentTime / duration) * 100 : 0;
  const bufferedPct = duration > 0 ? (buffered / duration) * 100 : 0;

  return (
    <div
      ref={wrapperRef}
      className={`dp-wrapper ${isFullscreen ? 'dp-fullscreen-active' : ''} ${showControls ? '' : 'dp-hide-controls'}`}
      onMouseMove={resetHideTimer}
      onMouseLeave={() => { if (playing) setShowControls(false); }}
    >
      <video
        ref={videoRef}
        src={streamUrl || undefined}
        preload="auto"
        playsInline
        onClick={handleVideoClick}
      />
      <audio ref={audioRef} src={audioStreamUrl || undefined} preload="auto" />

      {!videoReady && (
        <div className="dp-loading"><div className="dp-spinner" /></div>
      )}

      {!playing && videoRef.current?.paused && videoReady && (
        <div className="dp-center-play" onClick={togglePlay}>
          <div className="dp-center-play-icon">
            <svg viewBox="0 0 24 24"><polygon points="5,3 19,12 5,21" /></svg>
          </div>
        </div>
      )}

      <div className="dp-controls" onClick={(e) => e.stopPropagation()}>
        <div className="dp-progress-row" onMouseDown={handleSeekDown}>
          <div className="dp-progress-track">
            <div className="dp-progress-buffered" style={{ width: `${bufferedPct}%` }} />
            <div className="dp-progress-filled" style={{ width: `${progressPct}%` }} />
            <div className="dp-progress-thumb" style={{ left: `${progressPct}%` }} />
          </div>
        </div>

        <div className="dp-bar">
          <button onClick={togglePlay} aria-label={playing ? 'Pause' : 'Play'}>
            {playing ? (
              <svg viewBox="0 0 24 24"><rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" /></svg>
            ) : (
              <svg viewBox="0 0 24 24"><polygon points="5,3 19,12 5,21" /></svg>
            )}
          </button>

          <div className="dp-volume-group">
            <button onClick={toggleMute} aria-label={muted ? 'Unmute' : 'Mute'}>
              {muted || volume === 0 ? (
                <svg viewBox="0 0 24 24"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51A8.796 8.796 0 0 0 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06a8.99 8.99 0 0 0 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/></svg>
              ) : (
                <svg viewBox="0 0 24 24"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/></svg>
              )}
            </button>
            <input
              type="range"
              className="dp-volume-slider"
              min={0} max={1} step={0.05}
              value={muted ? 0 : volume}
              onChange={handleVolumeChange}
            />
          </div>

          <span className="dp-time">{formatTime(currentTime)} / {formatTime(duration)}</span>

          <div className="dp-spacer" />

          <button onClick={toggleFullscreen} aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
            {isFullscreen ? (
              <svg viewBox="0 0 24 24"><path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/></svg>
            ) : (
              <svg viewBox="0 0 24 24"><path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/></svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
